// Client-side GCIP/IAP chooser.
//
// Bundled by esbuild into dist/static/chooser.bundle.js; the server-rendered
// HTML at "/" loads this bundle via <script src="/static/chooser.bundle.js">.
//
// Drives the Google-published IAP+GCIP sign-in flow:
//
//   1. IAP redirects an unauthenticated user to /?apiKey=...&state=...&mode=...
//   2. gcip-iap's `Authentication` reads those params and routes to the right
//      handler method on our `AuthenticationHandler` below.
//   3. For sign-in, `startSignIn` renders one button per IdP from the
//      `idpConfigs` config island and calls
//      `auth.signInWithRedirect(provider)` on click. The browser bounces to
//      the IdP (Google or Microsoft Entra), back to this same URL, and ciap
//      completes the flow by re-calling `startSignIn` — which detects the
//      pending redirect via `getRedirectResult()` and resolves with the user.
//   4. ciap then bounces the Firebase ID token back to IAP, IAP mints its
//      session cookie on the dashboard domain, user is in.
//
// The image stays env-agnostic: tenant ID, project ID, API key, and IdP
// configs are all read from a server-injected JSON island
// (`<script id="login-config" type="application/json">`). No env-specific
// values are baked into this bundle.
//
// stratum-root#467 / gcp_platform_ailab_dashboard_gcip_pivot iteration_02
//   AC1 (gcip-iap library), AC3 (config island), AC7 (browser smoke).

import * as ciap from 'gcip-iap';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';

function readConfig() {
  const node = document.getElementById('login-config');
  if (!node) {
    throw new Error('login-config JSON island missing from HTML');
  }
  const parsed = JSON.parse(node.textContent || '{}');
  for (const required of ['apiKey', 'projectId', 'tenantId']) {
    if (!parsed[required]) {
      throw new Error(`login-config missing required field: ${required}`);
    }
  }
  if (!Array.isArray(parsed.idpConfigs) || parsed.idpConfigs.length === 0) {
    throw new Error('login-config.idpConfigs must be a non-empty array');
  }
  return parsed;
}

function showError(message) {
  const container = document.getElementById('firebaseui-auth-container');
  if (!container) return;
  container.innerHTML = '';
  const err = document.createElement('p');
  err.style.color = '#b00020';
  err.style.margin = '0';
  err.textContent = message;
  container.appendChild(err);
}

function showInfo(message) {
  const container = document.getElementById('firebaseui-auth-container');
  if (!container) return;
  const p = document.createElement('p');
  p.style.color = '#1f4d8a';
  p.style.margin = '0 0 0.75rem 0';
  p.textContent = message;
  container.appendChild(p);
}

function makeButton(idp) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = `Sign in with ${idp.label}`;
  btn.dataset.providerId = idp.providerId;
  btn.style.cssText = [
    'display:block',
    'width:100%',
    'padding:0.75rem 1rem',
    'margin:0.5rem 0',
    'border:1px solid #ddd',
    'background:#fff',
    'border-radius:4px',
    'cursor:pointer',
    'font-size:1rem',
    'font-family:inherit',
    'color:inherit',
  ].join(';');
  return btn;
}

function providerForIdpConfig(idp) {
  if (idp.providerId === 'google.com') {
    return new firebase.auth.GoogleAuthProvider();
  }
  if (idp.providerId.startsWith('oidc.') || idp.providerId.startsWith('saml.')) {
    return new firebase.auth.OAuthProvider(idp.providerId);
  }
  throw new Error(`Unsupported providerId: ${idp.providerId}`);
}

// Map a Firebase fetchSignInMethodsForEmail result back to our idpConfigs
// entry. fetchSignInMethodsForEmail returns provider IDs like 'google.com'
// or the full OIDC config name 'oidc.microsoft-entra'. Returns null if no
// matching IdP is configured (rare; would mean the existing user was
// created via a provider we no longer expose).
function findIdpByProviderId(idpConfigs, providerId) {
  return idpConfigs.find((idp) => idp.providerId === providerId) || null;
}

class AuthenticationHandler {
  constructor(config) {
    this.config = config;
  }

  // ciap calls this to obtain the Firebase Auth instance for a given
  // (apiKey, tenantId) pair. The pair always matches our config in this
  // single-tenant deployment, but ciap still requires the contract.
  getAuth(apiKey, tenantId) {
    let app;
    try {
      app = firebase.app();
    } catch (_) {
      app = firebase.initializeApp({
        apiKey,
        authDomain: `${this.config.projectId}.firebaseapp.com`,
        projectId: this.config.projectId,
      });
    }
    const auth = app.auth();
    if (tenantId) {
      auth.tenantId = tenantId;
    }
    return auth;
  }

  // ciap calls this when sign-in UI must be shown. Returns a Promise that
  // resolves with the UserCredential once sign-in completes.
  //
  // Why popup instead of redirect: signInWithRedirect persists auth state
  // via a cross-origin iframe at the project's authDomain
  // (`<project>.firebaseapp.com`). With modern browsers' third-party cookie
  // restrictions and the SameSite defaults, that persistence channel is
  // unreliable — the credential gets minted but `auth.onAuthStateChanged`
  // (which ciap polls via `getUser()`) never fires with the user, so ciap
  // can't reach `finishSignIn` to complete the IAP exchange. The visible
  // symptom is "user signs in via IdP, lands back on the chooser, buttons
  // re-render". Empirically observed against this nonprod tenant 2026-05-04.
  // signInWithPopup keeps everything in the originating browsing context, so
  // the credential is returned synchronously to this Promise without
  // depending on cross-origin storage. ciap then handles the IAP exchange.
  startSignIn(auth /*, match */) {
    return new Promise((resolve, reject) => {
      const container = document.getElementById('firebaseui-auth-container');
      if (!container) {
        reject(new Error('firebaseui-auth-container element missing'));
        return;
      }
      this.renderIdpButtons(auth, container, resolve);
    });
  }

  // Render one button per configured IdP. On click, run the popup sign-in;
  // on cross-provider conflict, swap the UI for a guided account-linking
  // flow that signs in with the existing provider and then attaches the new
  // credential via linkWithCredential. Resolves with the merged
  // UserCredential when complete.
  renderIdpButtons(auth, container, resolve) {
    container.innerHTML = '';
    for (const idp of this.config.idpConfigs) {
      const btn = makeButton(idp);
      btn.addEventListener('click', () => {
        this.runSignInWithPopup(auth, idp, container, resolve);
      });
      container.appendChild(btn);
    }
  }

  async runSignInWithPopup(auth, idp, container, resolve) {
    try {
      const provider = providerForIdpConfig(idp);
      const result = await auth.signInWithPopup(provider);
      if (!result || !result.user) {
        showError('Sign-in did not return a credential. Please try again.');
        return;
      }
      resolve(result);
    } catch (err) {
      // Popup-blocked, user-closed, or IdP error. Surface and let the
      // user retry rather than reject — ciap treats reject as a fatal
      // ciap error which would 503 the chooser.
      const code = err && err.code;
      if (code === 'auth/account-exists-with-different-credential') {
        // Cross-provider sign-in conflict: the email returned by this
        // IdP already has an account in our tenant under a different
        // provider. Standard Firebase Auth pattern is for the client to
        // sign in with the EXISTING provider first, then attach the new
        // credential via linkWithCredential. After linking, future
        // sign-ins via either provider resolve to the same user record.
        // The Console toggle "Link accounts that use the same email"
        // permits the merge; it does NOT auto-perform it.
        await this.handleAccountExistsWithDifferentCredential(auth, idp, err, container, resolve);
      } else if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        showError('Sign-in was cancelled. Try again when ready.');
      } else if (code === 'auth/popup-blocked') {
        showError('Browser blocked the sign-in popup. Allow popups for this site and try again.');
      } else {
        showError(`Sign-in failed: ${err.message || err}`);
      }
    }
  }

  async handleAccountExistsWithDifferentCredential(auth, attemptedIdp, err, container, resolve) {
    // Pull the pending credential + email out of the error. firebase-js
    // versions vary in where these live: v9+ exposes them on err directly;
    // v10 may use err.customData.email for the email. Try both.
    const pendingCred = err.credential || (err.customData && err.customData.credential);
    const email = err.email || (err.customData && err.customData.email);
    if (!pendingCred || !email) {
      showError(`Sign-in failed: ${err.message || err}`);
      return;
    }

    let methods;
    try {
      methods = await auth.fetchSignInMethodsForEmail(email);
    } catch (fetchErr) {
      showError(`Sign-in failed: ${fetchErr.message || fetchErr}`);
      return;
    }

    // Pick the first existing method that maps to one of our configured
    // IdPs. In practice fetchSignInMethodsForEmail returns the OIDC config
    // name verbatim (e.g. 'oidc.microsoft-entra') and 'google.com' for the
    // built-in Google IdP — both forms match providerForIdpConfig.
    const existingIdp = methods
      .map((providerId) => findIdpByProviderId(this.config.idpConfigs, providerId))
      .find(Boolean);

    if (!existingIdp) {
      showError(
        `An account already exists for ${email} but it was created with a sign-in method that is no longer enabled. Contact support to reset.`,
      );
      return;
    }

    // Render the link-flow UI: explanation + button to sign in with the
    // existing provider. On click, run that sign-in, link the pending
    // credential to the resulting user, and resolve as if it were a single
    // sign-in.
    container.innerHTML = '';
    showInfo(
      `An account for ${email} already exists, signed in via ${existingIdp.label}. ` +
        `To add ${attemptedIdp.label} to that account, sign in with ${existingIdp.label} below; ` +
        `we'll link the two automatically.`,
    );
    const linkBtn = makeButton({
      providerId: existingIdp.providerId,
      label: `Sign in with ${existingIdp.label} to link ${attemptedIdp.label}`,
    });
    linkBtn.addEventListener('click', async () => {
      try {
        const existingProvider = providerForIdpConfig(existingIdp);
        const existingResult = await auth.signInWithPopup(existingProvider);
        if (!existingResult || !existingResult.user) {
          showError('Sign-in did not return a credential. Please try again.');
          return;
        }
        try {
          await existingResult.user.linkWithCredential(pendingCred);
        } catch (linkErr) {
          if (linkErr && linkErr.code === 'auth/credential-already-in-use') {
            // Race: the pending credential already got attached on a
            // concurrent flow. Treat as success and proceed with the
            // existing-provider sign-in.
          } else {
            showError(`Linking failed: ${linkErr.message || linkErr}`);
            return;
          }
        }
        resolve(existingResult);
      } catch (popupErr) {
        const code = popupErr && popupErr.code;
        if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
          showError('Sign-in was cancelled. Try again when ready.');
        } else if (code === 'auth/popup-blocked') {
          showError('Browser blocked the sign-in popup. Allow popups for this site and try again.');
        } else {
          showError(`Sign-in failed: ${popupErr.message || popupErr}`);
        }
      }
    });
    container.appendChild(linkBtn);

    // Also add a "back" link that re-renders the original chooser, in
    // case the user wants to abandon the link attempt entirely.
    const backBtn = document.createElement('button');
    backBtn.type = 'button';
    backBtn.textContent = 'Cancel';
    backBtn.style.cssText = [
      'display:block',
      'width:100%',
      'padding:0.5rem 1rem',
      'margin:0.5rem 0 0 0',
      'border:none',
      'background:transparent',
      'color:#666',
      'cursor:pointer',
      'font-size:0.9rem',
      'font-family:inherit',
      'text-decoration:underline',
    ].join(';');
    backBtn.addEventListener('click', () => {
      this.renderIdpButtons(auth, container, resolve);
    });
    container.appendChild(backBtn);
  }

  // Optional ciap hooks. We keep them minimal; the underlying redirect
  // flow does the heavy lifting.
  selectTenant(_projectConfig, tenantIds) {
    // Single-tenant deployment: ciap will only ever pass our one tenant.
    return Promise.resolve({ tenantId: tenantIds[0] || this.config.tenantId });
  }

  completeSignOut() {
    return Promise.resolve();
  }

  processUser(user) {
    return Promise.resolve(user);
  }

  showProgressBar() {
    /* no-op — IdP redirect provides its own UI */
  }

  hideProgressBar() {
    /* no-op */
  }

  handleError(error) {
    // Last-resort error UI when ciap itself errors before/around sign-in.
    showError(`Authentication error: ${error.message || error}`);
  }
}

export function bootstrap() {
  let config;
  try {
    config = readConfig();
  } catch (err) {
    showError(err.message);
    return;
  }
  try {
    const handler = new AuthenticationHandler(config);
    const ciapInstance = new ciap.Authentication(handler);
    ciapInstance.start().catch((err) => {
      showError(`Initialization error: ${err.message || err}`);
    });
  } catch (err) {
    showError(`Initialization error: ${err.message || err}`);
  }
}

// Auto-bootstrap when loaded as a script. Tests can import bootstrap
// without auto-running by setting `window.__SKIP_CHOOSER_BOOTSTRAP__`
// before loading the bundle.
if (typeof window !== 'undefined' && !window.__SKIP_CHOOSER_BOOTSTRAP__) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap, { once: true });
  } else {
    bootstrap();
  }
}
